import { forwardRef, useEffect, useRef, useState } from "react";
import {
  constants,
  messages,
  LocateTabs,
  EndPoint,
  appColor,
} from "../../../common/constants";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import SpinnerFullScreen from "../../../components/SpinnerFullScreen";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import WindowedSelect from "react-windowed-select";
import {
  customFilterTicker,
  formatNumber,
  formatOptionLabel,
  getDivHeight,
  hasAccess,
  mapRowId,
} from "../../../common/commonFunctions";
import MuiAvatar from "@mui/material/Avatar";
import _ from "lodash";
import axios from "axios";
import moment from "moment";
import SLQuoteBorrowLoanGrid from "./BorrowLoanGrid";
import LocatesBarChart from "../../../components/LocatesBarChart";
import { Icon } from "@iconify/react";
import SingleEntry from "../../../components/SingleEntry";
import { serviceAPIMapping } from "../../../config/service-api-mapping";
import { getTickerTextField, isServiceHealthy } from "../../../utils/API";
import { pageConstant } from "../../../config/pageConstant";
import { useParams } from "react-router-dom";
import SLQuoteSupplyGrid from "./SupplyGrid";

interface DataType {
  icon: string;
  stats: number;
  title: string;
  color: any;
  id: string;
}

const sssRatesDefaults: DataType[] = [
  {
    stats: 0,
    title: "Avg borrow",
    color: "#1E355E",
    icon: "codicon:percentage",
    id: "ss_br_rate",
  },
  {
    color: "#1E355E",
    stats: 0,
    title: "Avg loan",
    icon: "iwwa:percentage",
    id: "ss_ln_rate",
  },
  {
    color: "#1E355E",
    stats: 0,
    title: "Locate rate",
    icon: "la:percentage",
    id: "ss_locate_rate",
  },
  {
    stats: 0,
    color: "#1E355E",
    title: "Cents per share",
    icon: "fluent-mdl2:calculator-percentage",
    id: "cents_share",
  },
];

const lpRatesMaster: DataType[] = [
  {
    stats: 0,
    title: "LP Out(%)",
    color: "#1E355E",
    icon: "iwwa:percentage",
    id: "lp_rate_out",
  },
  {
    color: "#1E355E",
    stats: 0,
    title: "LP New(%)",
    icon: "carbon:percentage",
    id: "lp_rate_new",
  },
  {
    color: "#1E355E",
    stats: 0,
    title: "LP Intra(%)",
    icon: "mynaui:percentage",
    id: "lp_rate_last",
  },
  {
    color: "#1E355E",
    stats: 0,
    title: "Utilization(%)",
    icon: "codicon:percentage",
    id: "utilization",
  },
];

const dtcStatsDefaults: DataType[] = [
  {
    stats: 0,
    title: "Box Qty",
    color: "#1E355E",
    icon: "material-symbols:inventory-2-outline-rounded",
    id: "box_qty",
  },
  {
    color: "#1E355E",
    stats: 0,
    title: "Seg Qty",
    icon: "ic:baseline-inventory",
    id: "seg_qty",
  },
];
const callbackStatsDefaults: DataType[] = [
  {
    stats: 0,
    title: "Callbacks",
    color: "#1E355E",
    icon: "material-symbols:phone-callback",
    id: "callbacks_cnt",
  },
  {
    color: "#1E355E",
    stats: 0,
    title: "Recalls",
    icon: "hugeicons:delivery-return-02",
    id: "recall_cnt",
  },
];
const pnlStatsDefaults: DataType[] = [
  {
    stats: 0,
    title: "Match Book",
    color: "#1E355E",
    icon: "healthicons:dollar",
    id: "matchbook_pnl",
  },
  {
    stats: 0,
    title: "Locate",
    color: "#1E355E",
    icon: "healthicons:dollar",
    id: "locate_pnl",
  },
];

const mbStatsDefaults: DataType[] = [
  {
    stats: 0,
    title: "Avg Borrow",
    color: "#1E355E",
    icon: "iwwa:percentage",
    id: "sss_mb_borr_rate",
  },
  {
    color: "#1E355E",
    stats: 0,
    title: "Avg Loan",
    icon: "carbon:percentage",
    id: "sss_mb_loan_rate",
  },
];

export const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    color: "#000",
    fontSize: "0.7rem",
    backgroundColor: state.isFocused ? "#EAECEF" : null,
    cursor: "pointer",
  }),
  control: (base: any) => ({
    ...base,
    fontSize: "0.8rem",
    minWidth: "18rem",
    marginRight: "0.75rem",
    borderColor: "#EAECEF",
    ":hover": {
      borderColor: "#EAECEF",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    height: "2.4rem",
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
  }),
  menu: (provided: any) => ({
    ...provided,
    width: 300,
    backgroundColor: "#fff",
  }),
  input: (provided: any) => ({
    ...provided,
    height: "2rem",
  }),
};

const SLQuote = () => {
  const [open, setOpen] = useState(false);
  const topDivRef = useRef(null);
  const [snackbarMessage, setSnackbarMessage] = useState<any>("");
  const [tickerList, setTickerList] = useState<any>();
  const [selectedTickerInfo, setSelectedTickerInfo] = useState<any>();
  const [selectedTicker, setSelectedTicker] = useState<any>("");
  const [selectedCusip, setSelectedCusip] = useState<any>("");
  const [isBookTradeOpen, setIsBookTradeOpen] = useState<boolean>(false);
  const [isLocatesVisible, setIsLocatesVisible] = useState<boolean>(false);
  const [lpRates, setLPRates] = useState<any[]>(lpRatesMaster);
  const [borrows, setBorrows] = useState<any[]>([]);
  const [loans, setLoans] = useState<any[]>([]);
  const [sssRates, setSSSRates] = useState<any[]>(sssRatesDefaults);
  const [dtcStats, setDtcStats] = useState<any[]>(dtcStatsDefaults);
  const [mbStats, setMBStats] = useState<any[]>(mbStatsDefaults);
  const [callbackStats, setCallbackStats] = useState<any[]>(
    callbackStatsDefaults
  );
  const [pnlStats, setPnlStats] = useState<any[]>(pnlStatsDefaults);
  const [supplyStats, setSupplyStats] = useState<any[]>([]);
  const [supplyLenders, setSupplyLenders] = useState<any[]>([]);
  const [supplyValues, setSupplyValues] = useState<any[]>([]);
  const initialKpiInfo = {
    value: "",
    label: "",
    name: "",
    price: "",
    date: "",
    cusip: "",
    ticker: "",
    isregsho:'',
    islocatehold:''
  };
  const [kpiInfo, setKpiInfo] = useState<any>({ ...initialKpiInfo });
  const [inputTrade, setInputTrade] = useState<any>(null);
  const accountId = window.localStorage.getItem("activeAccountCode");
  const [isContractBookingLoading, setIsContractBookingLoading] =
    useState(false);
  const tickerRef = useRef<any>(null);
  const { ticker_in } = useParams();
  const ref = useRef(null);
  const [tabData, setTabData] = useState<any>([
    {
      type: LocateTabs.locatesApproved,
      series: [{ data: [] }],
      labels: [],
    },
    {
      type: LocateTabs.locatesUsed,
      series: [{ data: [] }],
      labels: [],
    },
    {
      type: LocateTabs.preBorrow,
      series: [{ data: [] }],
      labels: [],
    },
  ]);
  const [isSmallerDeviceRes, setIsSmallerDeviceRes] = useState<boolean>(
    window?.innerWidth < 992
  );


  useEffect(() => {
    setIsLocatesVisible(true);
  }, [ref?.current]);

  const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref
  ) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === constants.clickaway) {
      return;
    }

    setOpen(false);
  };

  const onChangeTicker = (value: any) => {
    const splitValue = value.value.split("/");
    const selectedKPI = {
      value: splitValue[0],
      label: splitValue[1],
      name: value.name,
      price: value.price,
      date: value.date,
      cusip: value.cusip,
      ticker: value.ticker,
    };
    setInputTrade(value);
    setSelectedTickerInfo(selectedKPI);
  };

  const renderLp = () => {
    return lpRates.map((data: DataType, index: number) => (
      <div className="d-flex justify-content-between" key={index}>
        <div className="d-flex flex-row" key={index}>
          <MuiAvatar sx={{ ml: 1, width: 34, height: 34 }}>
            <Icon
              style={{ fontSize: "1rem" }}
              icon={data?.icon}
              color={data?.color}
            ></Icon>
          </MuiAvatar>
          <div className="d-flex flex-column">
            <div className="sl-quote-kpi-value">
              <span>{data.stats}</span>
            </div>
            <div className="sl-quote-kpi-title">
              <span>{data.title}</span>
            </div>
            {/* <Typography fontSize={"0.8rem"} fontWeight={600}>
              {data.stats}
            </Typography> */}
            {/* <Typography fontSize={"0.8rem"}>{data.title}</Typography> */}
          </div>
        </div>
      </div>
    ));
  };

  const onTradeBook = () => {
    setIsBookTradeOpen(true);
  };

  const renderStats = () => {
    return sssRates.map((data: DataType, index: number) => (
      <div className="d-flex justify-content-between" key={index}>
        <div className="d-flex flex-row" key={index}>
          <MuiAvatar sx={{ ml: 1, width: 34, height: 34 }}>
            <Icon
              style={{ fontSize: "1rem" }}
              icon={data?.icon}
              color={data?.color}
            ></Icon>
          </MuiAvatar>
          <div className="d-flex flex-column">
            <div className="sl-quote-kpi-value">
              <span>{data.stats}</span>
            </div>
            <div className="sl-quote-kpi-title">
              <span>{data.title}</span>
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const renderMBStats = () => {
    return mbStats.map((data: DataType, index: number) => (
      <div className="d-flex justify-content-between" key={index}>
        <div className="d-flex flex-row" key={index}>
          <MuiAvatar sx={{ ml: 1, width: 34, height: 34 }}>
            <Icon
              style={{ fontSize: "1rem" }}
              icon={data?.icon}
              color={data?.color}
            ></Icon>
          </MuiAvatar>
          <div className="d-flex flex-column">
            <div className="sl-quote-kpi-value">
              <span>{data?.stats?.toFixed(3)}</span>
            </div>
            <div className="sl-quote-kpi-title">
              <span>{data.title}</span>
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const renderDTCStats = () => {
    return dtcStats.map((data: DataType, index: number) => (
      <div className="d-flex justify-content-between" key={index}>
        <div className="d-flex flex-row" key={index}>
          <MuiAvatar sx={{ ml: 1, width: 34, height: 34 }}>
            <Icon
              style={{ fontSize: "1rem" }}
              icon={data?.icon}
              color={data?.color}
            ></Icon>
          </MuiAvatar>
          <div className="d-flex flex-column">
            <div className="sl-quote-kpi-value">
              <span>{data.stats}</span>
            </div>
            <div className="sl-quote-kpi-title">
              <span>{data.title}</span>
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const renderCallbacksStats = () => {
    return callbackStats.map((data: DataType, index: number) => (
      <div className="d-flex justify-content-between" key={index}>
        <div className="d-flex flex-row" key={index}>
          <MuiAvatar sx={{ ml: 1, width: 34, height: 34 }}>
            <Icon
              style={{ fontSize: "1rem" }}
              icon={data?.icon}
              color={data?.color}
            ></Icon>
          </MuiAvatar>
          <div className="d-flex flex-column">
            <div className="sl-quote-kpi-value">
              <span>{data.stats}</span>
            </div>
            <div className="sl-quote-kpi-title">
              <span>{data.title}</span>
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const renderPNLStats = () => {
    return pnlStats.map((data: DataType, index: number) => (
      <div className="d-flex justify-content-between" key={index}>
        <div className="d-flex flex-row" key={index}>
          <MuiAvatar sx={{ ml: 1, width: 34, height: 34 }}>
            <Icon
              style={{ fontSize: "1rem" }}
              icon={data?.icon}
              color={data?.color}
            ></Icon>
          </MuiAvatar>
          <div className="d-flex flex-column">
            <div className="sl-quote-kpi-value">
              <span>
                {data.stats && !_.isNaN(data.stats)
                  ? formatNumber.format(data.stats)
                  : formatNumber.format(0)}
              </span>
            </div>
            <div className="sl-quote-kpi-title">
              <span>{data.title}</span>
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const getTickerList = async () => {
    const options: any = [];

    var tickerHeader = new Headers();
    const token = window.localStorage.getItem("refreshtoken");

    tickerHeader.append("Content-Type", "application/json");
    tickerHeader.append("Authorization", `Bearer ${token}`);

    await getTickerTextField().then(async (response: any) => {
      if (!response) {
        setOpen(true);
        setSnackbarMessage(messages.TickerDownMessage);
      } else if (response.status === 200) {
        const tickerdataApi: any = await response?.json();
        for (let i = 0; i < tickerdataApi.length; i++) {
          const tempOptions: any = {
            value: tickerdataApi[i].ticker + "/" + tickerdataApi[i].cusip,
            label: tickerdataApi[i].name,
            ticker: tickerdataApi[i].ticker,
            cusip: tickerdataApi[i].cusip,
            name: tickerdataApi[i].name,
            price: tickerdataApi[i].price,
          };
          options.push(tempOptions);
        }
        setTickerList(options);
      } else if (response.status === 401) {
      }
    });
  };

  useEffect(() => {
    if (selectedTickerInfo) {
      setKpiInfo(selectedTickerInfo);
      setSelectedTicker(selectedTickerInfo?.ticker);
      setSelectedCusip(selectedTickerInfo?.cusip)
    }
  }, [selectedTickerInfo]);

  useEffect(() => {
    tickerRef?.current?.focus();
     getSecMasterHealthCheck()
     getReportingServiceHealthCheck()
  }, []);

  const getSecMasterHealthCheck = async()=>{
    await isServiceHealthy(`${serviceAPIMapping["SECMASTER-SERVICE"].apiRouteURL}/${EndPoint.actuator}`).then((response: any)=>{
     if(!response?.ok || response?.status === 503){
        setSnackbarMessage(messages.TickerDownMessage)
        setOpen(true)
     } else{
        getTickerList();
     }
    })
   
  }

  const getReportingServiceHealthCheck = async () => {
    await isServiceHealthy(`${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}/${EndPoint.actuator}`).then((response: any)=>{
       if(!response?.ok || response?.status === 503){
        setSnackbarMessage(`Reporting${messages.serviceDownMessage}`)
        setOpen(true)
     }
    })
  }

  useEffect(() => {
    if (selectedTicker && !_.isEmpty(selectedTicker)) {
      const request = {
        function_code: "fn_rpt_sl_quote_ticker_cusip",
        function_param: {
          ticker_in: selectedTicker,
          accountid: accountId,
          cusip_in: selectedCusip
        },
      };
      getLPRates(request);
      getBorrowsAndLoans();
      getBarChartInfo();
      getSupplyStats();
    } else setKpiInfo(initialKpiInfo);
  }, [selectedTicker]);

  const getBorrowsAndLoans = async () => {
    if (selectedTicker && !_.isEmpty(selectedTicker)) {
      const borrowRequest = {
        function_code: "fn_rpt_getopencontracts",
        function_param: {
          bl: "B",
          date: moment().toDate(),
          accountid: accountId,
          ticker: selectedTicker,
        },
      };

      const token = window.localStorage.getItem("refreshtoken");

      const header = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const borrowsResult = await axios.post(
        `${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}` +
          EndPoint.reportFn,
        borrowRequest,
        {
          headers: header,
        }
      );
      if (borrowsResult && borrowsResult?.status === 200) {
        const borrowsRes = borrowsResult?.data?.map((item: any) => {
          return {
            ...item,
            id: item?.contract_id,
          };
        });
        setBorrows(borrowsRes);
      }
      const loanRequest = {
        function_code: "fn_rpt_getopencontracts",
        function_param: {
          bl: "L",
          date: moment().toDate(),
          accountid: accountId,
          ticker: selectedTicker,
        },
      };

      const loanResult = await axios.post(
        `${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}` +
          EndPoint.reportFn,
        loanRequest,
        { headers: header }
      );
      if (loanResult && loanResult?.status === 200) {
        const loanRes = loanResult?.data?.map((item: any) => {
          return {
            ...item,
            id: item?.contract_id,
          };
        });
        setLoans(loanRes);
      }
    }
  };

  const getLPRates = async (request: any) => {
    const token = window.localStorage.getItem("refreshtoken");

    const header = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const getTickerTextFieldRes = await axios.post(
      `${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}` +
        EndPoint.reportFn,
      request,
      {
        headers: header,
      }
    );
    if (
      getTickerTextFieldRes &&
      getTickerTextFieldRes?.status === 200 &&
      _.isArray(getTickerTextFieldRes?.data) &&
      getTickerTextFieldRes?.data?.length > 0
    ) {
      const lpRate = getTickerTextFieldRes?.data[0];
      const lpRateResult = lpRates?.map((item: DataType) => {
        return {
          ...item,
          stats: _.get(lpRate, item?.id) ? _.get(lpRate, item?.id) : 0,
        };
      });
      const lpRateList = lpRateResult.map((item: DataType) => {
        if (item.id === "utilization")
          return {
            ...item,
            stats: item.stats,
          };

        return {
          ...item,
          stats: item.stats.toFixed(3),
        };
      });
      setLPRates(lpRateList);

      const sssRatesResult = sssRatesDefaults?.map((item: DataType) => {
        return {
          ...item,
          stats: _.get(lpRate, item?.id) ? _.get(lpRate, item?.id) : 0,
        };
      });

      setSSSRates(sssRatesResult);

      const dtcStatsResult = dtcStatsDefaults?.map((item: DataType) => {
        return {
          ...item,
          stats: _.get(lpRate, item?.id) ? _.get(lpRate, item?.id) : 0,
        };
      });

      setDtcStats(dtcStatsResult);

      const callbackStatsResult = callbackStatsDefaults?.map(
        (item: DataType) => {
          return {
            ...item,
            stats: _.get(lpRate, item?.id) ? _.get(lpRate, item?.id) : 0,
          };
        }
      );

      setCallbackStats(callbackStatsResult);

      const pnlStatsResult = pnlStatsDefaults?.map((item: DataType) => {
        return {
          ...item,
          stats: _.get(lpRate, item?.id) ? _.get(lpRate, item?.id) : 0,
        };
      });

      setPnlStats(pnlStatsResult);

      const mbRatesResult = mbStatsDefaults?.map((item: DataType) => {
        return {
          ...item,
          stats: _.get(lpRate, item?.id) ? _.get(lpRate, item?.id) : 0,
        };
      });

      setMBStats(mbRatesResult);

      setKpiInfo((tickerData: any)=>{
        if(lpRate?.ticker === tickerData?.ticker){
          tickerData.isregsho = lpRate?.isregsho
          tickerData.islocatehold = lpRate?.islocatehold
        }

        return tickerData
      })
    }
  };

  const getSupplyStats = async () => {
    const supplyRequest = {
      function_code: "fn_rpt_getlender_supply",
      function_param: {
        date_in: moment().format("YYYY-MM-DD"),
        ticker_in: selectedTicker,
        account_id: accountId,
      },
    };

    const token = window.localStorage.getItem("refreshtoken");

    const header = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const supplyResult = await axios.post(
      `${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}` +
        EndPoint.reportFn,
      supplyRequest,
      {
        headers: header,
      }
    );
    if (supplyResult && supplyResult?.status === 200) {
      const supplyData = _.take(
        _.orderBy(supplyResult?.data, "qty", "desc"),
        10
      );
      const mappedRows = mapRowId(supplyData);
      setSupplyStats(mappedRows);
      const supplies = supplyData;

      //setFilteredSupplyStats(supplies)
      const lenders = supplies?.map((supply: any) => {
        return supply?.lender;
      });

      setSupplyLenders(lenders);
      const quantities = supplies?.map((supply: any) => {
        return supply?.value;
      });

      setSupplyValues(quantities);
    }
  };

  const renderSupply = () => {
    return (
      <div className="h-100 w-100 p-0 pt-1">
        <SLQuoteSupplyGrid rowData={supplyStats} />
      </div>
    );
  };

  useEffect(() => {
    if (!_.isEmpty(tickerList)) {
      const msftTicker: any = tickerList.find(
        (t: any) => t.ticker === ticker_in
      );
      if (msftTicker) {
        const splitValue = msftTicker?.value?.split("/");
        const selectedKPI = {
          value: splitValue[0],
          label: splitValue[1],
          name: msftTicker.name,
          price: msftTicker.price,
          date: msftTicker.date,
          cusip: msftTicker.cusip,
          ticker: msftTicker.ticker,
        };
        setInputTrade(msftTicker);
        setSelectedTickerInfo(selectedKPI);
      }
    }
  }, [tickerList]);

  const getBarChartInfo = async () => {
    const [preBorrowLabels, preBorrowValues] = await getPreBorrows();
    const [usedLocatesLabels, usedLocatesValues] = await getUsedLocates();
    const [approvedLocateLabels, approvedLocateValues] =
      await getApprovedLocates();
    setTabData((currentTabInfo: any) => {
      const newTabInfo = [...currentTabInfo];
      let index = newTabInfo?.findIndex(
        (t: any) => t.type === LocateTabs.preBorrow
      );
      if (index > -1) {
        const preBorrowTab = {
          type: LocateTabs.preBorrow,
          series: [{ data: preBorrowValues }],
          labels: preBorrowLabels,
        };
        newTabInfo[index] = preBorrowTab;
      }
      index = newTabInfo?.findIndex(
        (t: any) => t.type === LocateTabs.locatesUsed
      );
      if (index > -1) {
        const locatesUsedTab = {
          type: LocateTabs.locatesUsed,
          series: [{ data: usedLocatesValues }],
          labels: usedLocatesLabels,
        };
        newTabInfo[index] = locatesUsedTab;
      }
      index = newTabInfo?.findIndex(
        (t: any) => t.type === LocateTabs.locatesApproved
      );
      if (index > -1) {
        const approvedTab = {
          type: LocateTabs.locatesApproved,
          series: [{ data: approvedLocateValues }],
          labels: approvedLocateLabels,
        };
        newTabInfo[index] = approvedTab;
      }

      return newTabInfo;
    });
  };

  const getPreBorrows = async () => {
    if (selectedTicker && !_.isEmpty(selectedTicker)) {
      const borrowRequest = {
        function_code: "fn_rpt_get_prebrrows grouping",
        function_param: {
          date: moment().format("YYYY-MM-DD"),
          sec_id: selectedTicker,
          //accountid_in: accountId,
        },
      };

      const token = window.localStorage.getItem("refreshtoken");

      const header = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const borrowsResult = await axios.post(
        `${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}` +
          EndPoint.reportFn,
        borrowRequest,
        {
          headers: header,
        }
      );
      if (borrowsResult && borrowsResult?.status === 200) {
        return [
          _.map(borrowsResult?.data, "cpty"),
          _.map(borrowsResult?.data, "quantity"),
        ];
      }
    }

    return [0, 0];
  };
  const getUsedLocates = async () => {
    if (selectedTicker && !_.isEmpty(selectedTicker)) {
      const locateRequest = {
        function_code: "fn_rpt_get_locates_used grouping",
        function_param: {
          date: moment().format("YYYY-MM-DD"),
          sec_id: selectedTicker,
          //accountid_in: accountId,
        },
      };

      const token = window.localStorage.getItem("refreshtoken");

      const header = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const locatesResult = await axios.post(
        `${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}` +
          EndPoint.reportFn,
        locateRequest,
        {
          headers: header,
        }
      );
      if (locatesResult && locatesResult?.status === 200) {
        return [
          _.map(locatesResult?.data, "client"),
          _.map(locatesResult?.data, "quantity"),
        ];
      }
    }

    return [0, 0];
  };

  const getApprovedLocates = async () => {
    if (selectedTicker && !_.isEmpty(selectedTicker)) {
      const locatesRequest = {
        function_code: "fn_ui_locates_approved_by_ticker",
        function_param: {
          date_in: moment().format("YYYY-MM-DD"),
          sec_id_in: selectedTicker,
          //accountid: accountId,
        },
      };

      const token = window.localStorage.getItem("refreshtoken");

      const header = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const locatesResult = await axios.post(
        `${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}` +
          EndPoint.reportFn,
        locatesRequest,
        {
          headers: header,
        }
      );
      if (locatesResult && locatesResult?.status === 200) {
        return [
          _.map(locatesResult?.data, "client"),
          _.map(locatesResult?.data, "quantity"),
        ];
      }
    }

    return [0, 0];
  };

  const onBookTradeClose = (event: any, reason: any) => {
    if (reason && (reason == "backdropClick" || reason == "escapeKeyDown")) {
      return;
    }
    setIsBookTradeOpen(false);
    getBorrowsAndLoans();
  };

  const onTradeBookClose = () => {
    setIsBookTradeOpen(false);
    setTimeout(() => {
      getBorrowsAndLoans();
    }, 200);
  };

  useEffect(() => {
    // Function to handle the resize event
    function handleResize() {
      setIsSmallerDeviceRes(window.innerWidth <= 992);
    }

    // Add the event listener
    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={{ height: "100%" }}>
      {isContractBookingLoading && <SpinnerFullScreen />}
      <Snackbar open={open} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity="error"
          sx={{
            width: "100%",
            backgroundColor: "orange",
            textDecorationColor: "white",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <div ref={topDivRef} id="eod-kpis" className="p-0">
        <div
          className={`d-flex flex-row sm-md-overflow-x  ${
            isSmallerDeviceRes? "row justify-content-start" : ""
          }`}
        >
          <div className={`${isSmallerDeviceRes ? "col pt-1" : ""}`}>
            <WindowedSelect
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              }}
              tabIndex={0}
              onChange={(value: any) => {
                onChangeTicker(value);
              }}
              placeholder="Search..."
              options={tickerList}
              formatOptionLabel={formatOptionLabel}
              windowThreshold={0}
              filterOption={customFilterTicker}
              styles={customStyles}
              value={selectedTickerInfo}
              getOptionLabel={(option: any) => option.ticker}
              getOptionValue={(option: any) => option.ticker}
              id="tickerMatch"
              ref={tickerRef}
            />
          </div>
          <div
            className={`d-flex flex-row align-items-center ${
              isSmallerDeviceRes ? " row" : ""
            }`}
          >
            <Button
              onClick={() => {
                setInputTrade({ ...inputTrade, isMatchTrade: false });
                onTradeBook();
              }}
              className={`ms-2 ${
                isSmallerDeviceRes ? "mt-1 col" : ""
              }`}
              size="small"
              variant="contained"
              sx={{
                backgroundColor: "#1E355E",
                fontSize: "0.7rem",
                "&:hover": { backgroundColor: "#1E355E" },
              }}
              disabled={
                !hasAccess([
                  `${pageConstant?.TRADE_BOOKING}_ADMIN`,
                  `${pageConstant?.TRADE_BOOKING}_RW`,
                ])
              }
            >
              Book a trade
            </Button>
            <Typography
              fontSize={"1rem"}
              className={`ms-3 ${
                isSmallerDeviceRes ? " col" : ""
              }`}
              variant="h6"
              color="#1E355E"
            >
              Name:
            </Typography>
            <Typography fontSize={"1rem"}  className={`ms-1 ${
                isSmallerDeviceRes ? " col text-nowrap" : ""
              }`} variant="h6">
              {kpiInfo?.name}
            </Typography>
            
            <div className={`d-flex flex-row ms-5 ${
                  isSmallerDeviceRes ? " col" : ""
                }`}>
              <Typography
                fontSize={"1rem"}
                className="ms-1"
                variant="h6"
                color="#1E355E"
              >
                Cusip:
              </Typography>
              <Typography fontSize={"1rem"} className="ms-1" variant="h6">
                {kpiInfo?.cusip}
              </Typography>
            </div>
            <div className={`d-flex flex-row ms-5 ${
                  isSmallerDeviceRes ? " col" : ""
                }`}>
              <Typography
                color="#1E355E"
                fontSize={"1rem"}
                className="ms-1"
                variant="h6"
              >
                Ticker:
              </Typography>
              <Typography fontSize={"1rem"} className="ms-1" variant="h6">
                {kpiInfo?.ticker}
              </Typography>
            </div>
            <div className={`d-flex flex-row ms-5 ${
                  isSmallerDeviceRes ? " col" : ""
                }`}>
              <Typography
                color="#1E355E"
                fontSize={"1rem"}
                className="ms-1"
                variant="h6"
              >
                Price:
              </Typography>
              <Typography fontSize={"1rem"} className="ms-1" variant="h6">
                {formatNumber.format(kpiInfo?.price)}
              </Typography>
            </div>
            <div
            className={`d-flex flex-row ms-5 ${
              isSmallerDeviceRes ? " col" : ""
            }`}
          >
            <Typography color={appColor} fontSize={"1rem"} variant="h6">
              Regsho:{" "}
            </Typography>
            <Typography className="ms-2" fontSize={"1rem"} color={kpiInfo?.isregsho?'red':''}>{`${
              kpiInfo?.isregsho
                ? `Yes`
                : "No"
            }`}</Typography>
          </div>
          <div
            className={`d-flex flex-row ms-5 ${
              isSmallerDeviceRes ? " col" : ""
            }`}
          >
            <Typography color={appColor} fontSize={"1rem"} variant="h6">
              Locate Hold:{" "}
            </Typography>
            <Typography className="ms-2" fontSize={"1rem"} color={kpiInfo?.islocatehold?'red':''}>{`${
              kpiInfo?.islocatehold
                ? `Yes`
                : "No"
            }`}</Typography>
          </div>
          </div>
        </div>
        <div className="d-flex flex-row mt-1 row col-xxl-12 col-xl-12 col-lg-12 ms-1 me-1" style={{width:'99.8%'}}>
          <div
            className="p-0 pb-1 col-lg-3 col-xl-3 col-xxl-3 me-1 col-sm-6 col-md-6"
            style={{ overflowX: "auto" }}
          >
            <Card>
              <CardHeader
                className="p-0 ps-2"
                subheader={
                  <Typography sx={{ color: appColor }}>
                    Lending pit Rates
                  </Typography>
                }
              ></CardHeader>
              <CardContent className="p-0 pe-1 d-flex justify-content-between">
                {renderLp()}
              </CardContent>
            </Card>
          </div>
          <div
            className="p-0 col-lg-3 col-xl-3 col-xxl-3 me-1 col-sm-6 col-md-6"
            style={{ overflowX: "auto" }}
          >
            <Card>
              <CardHeader
                className="p-0 ps-2"
                subheader={
                  <Typography sx={{ color: appColor }}>SSS Rates</Typography>
                }
              ></CardHeader>
              <CardContent className="p-0 pe-1 d-flex justify-content-between">
                {renderStats()}
              </CardContent>
            </Card>
          </div>
          <div className="col p-0 me-1 sm-md-overflow-x sm-md-mt-2-rem">
            <Card>
              <CardHeader
                className="p-0 ps-2"
                subheader={
                  <Typography sx={{ color: appColor }}>DTC</Typography>
                }
              ></CardHeader>
              <CardContent className="p-0 d-flex justify-content-around">
                {renderDTCStats()}
              </CardContent>
            </Card>
          </div>
          <div className="col p-0 me-1 sm-md-overflow-x sm-md-mt-2-rem">
            <Card>
              <CardHeader
                className="p-0 ps-2"
                subheader={
                  <Typography sx={{ color: appColor }}>Callbacks</Typography>
                }
              ></CardHeader>
              <CardContent className="p-0 d-flex justify-content-around">
                {renderCallbacksStats()}
              </CardContent>
            </Card>
          </div>
          <div className="col p-0 sm-md-overflow-x sm-md-mt">
            <Card>
              <CardHeader
                className="p-0 ps-2"
                subheader={
                  <Typography sx={{ color: appColor }}>PNL</Typography>
                }
              ></CardHeader>
              <CardContent className="p-0 d-flex justify-content-around">
                {renderPNLStats()}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <div
        className="row p-0 mx-1 pe-1"
        style={{ height: getDivHeight("eod-kpis", 10) }}
      >
        <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12 col-sm-12 p-0 sm-md-mt-2-rem">
          <div
            className="w-100 mb-1 row ms-0 me-0"
            style={{ height: isSmallerDeviceRes ? "50vh" : "50%" }}
          >
            <Card className="p-0 ps-1 pb-1">
              <CardHeader
                className="p-0 ps-1"
                id="borrow-header"
                subheader={
                  <div className="d-flex justify-content-start">
                    <Typography className="pt-1" sx={{ color: "#1E355E" }}>
                      Supply / Availability
                    </Typography>
                  </div>
                }
              ></CardHeader>
              <CardContent className="w-100 h-100 d-flex flex-row p-0">
                <Card className="w-100 h-100 p-0 pb-1">
                  <CardContent className="w-100 h-100 d-flex flex-row p-0 pb-1">
                    <div className="w-100 h-100">{renderSupply()}</div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </div>
          <div
            className="mt-1 sm-md-mt me-0"
            style={{ height: isSmallerDeviceRes ? "50vh" : "50%", width:'100%' }}
          >
            <div style={{ height: "100%" }}>
              <LocatesBarChart data={tabData} />
            </div>
          </div>
        </div>
        <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12 col-sm-12 p-0 ms-0 row">
          <div className="w-100 sm-md-mt-2-rem p-0 ms-1">
            <div className="h-100">
              <Card
                className="p-1"
                style={{ height: isSmallerDeviceRes ? "45vh" : "100%" }}
              >
                <CardHeader
                  className="p-0 ps-2"
                  id="borrow-header"
                  subheader={
                    <div className="d-flex justify-content-between">
                      <Typography className="pt-1" sx={{ color: "#1E355E" }}>
                        Borrows
                      </Typography>
                    </div>
                  }
                ></CardHeader>
                <CardContent
                  sx={{ height: getDivHeight("borrow-header") }}
                  className="p-0 mt-1"
                >
                  <SLQuoteBorrowLoanGrid rowData={borrows} />
                </CardContent>
              </Card>
            </div>
          </div>
          <div className="w-100 p-0 mt-1 ms-1">
            <div
              className="h-100 w-100"
              style={{ marginTop: isSmallerDeviceRes ? "4px" : "" ,height: isSmallerDeviceRes ? "50vh" : "50%"}}
            >
              <Card className="p-1 h-100">
                <CardHeader
                  className="p-0 ps-2"
                  id="loan-header"
                  subheader={
                    <div className="d-flex justify-content-between">
                      <Typography className="pt-1" sx={{ color: "#1E355E" }}>
                        Loans
                      </Typography>
                    </div>
                  }
                ></CardHeader>
                <CardContent
                  className="p-0 mt-1"
                  sx={{ height: getDivHeight("loan-header") }}
                >
                  <SLQuoteBorrowLoanGrid rowData={loans} />
                </CardContent>
              </Card>
            </div>
          </div>

          <Dialog
        open={isBookTradeOpen}
        onClose={onBookTradeClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="rows-skipped"
        fullWidth
        maxWidth="xl"
        scroll="body"
        PaperProps={{ sx: { maxWidth: "1400px" } }}
      >
        <DialogTitle
          className="p-2"
          id="alert-dialog-title"
          sx={{ backgroundColor: "#1E355E" }}
        >
          <div className="d-flex justify-content-between">
            <Typography sx={{ color: "white" }}>Book a Trade</Typography>
            <Icon
              icon="material-symbols-light:close"
              color="white"
              role="button"
              onClick={() => {
                setIsBookTradeOpen(false);
              }}
            ></Icon>
          </div>
        </DialogTitle>
        <DialogContent>
          <SingleEntry
            trade={inputTrade}
            onCloseBookTrade={onTradeBookClose}
            setIsContractBookingLoading={(isLoading: boolean) =>
              setIsContractBookingLoading(isLoading)
            }
          />
        </DialogContent>
      </Dialog>
        </div>
      </div>
    </div>
  );
};

export default SLQuote;
